import axios from 'axios';

// Note: Request queue removed as it was causing axios interceptors to hang
// Axios interceptors should return config synchronously, not Promises

// Retry configuration
const retryConfig = {
    retries: 3,
    retryDelay: (retryCount) => {
        // Exponential backoff: 1s, 2s, 4s
        return Math.pow(2, retryCount) * 1000;
    },
    retryCondition: (error) => {
        // Retry on 429 (Too Many Requests) and network errors
        return error.response?.status === 429 || 
               error.code === 'NETWORK_ERROR' ||
               error.code === 'ECONNABORTED' ||
               error.code === 'ECONNRESET' ||
               error.code === 'ETIMEDOUT' ||
               error.message?.includes('Network Error') ||
               error.message?.includes('timeout') ||
               error.message?.includes('Request aborted') ||
               error.message?.includes('aborted') ||
               !error.response; // No response usually means network issue
    }
};

// Create axios instance with default config
const axiosInstance = axios.create({
    timeout: 30000, // 30 second timeout
    // Don't set Content-Type header - let axios handle it automatically
    // This allows FormData to use multipart/form-data and regular data to use application/x-www-form-urlencoded
});

// Set up global unhandled rejection handler
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled Promise Rejection:', event.reason);
        
        // Check if it's an axios-related error
        const isAxiosError = event.reason && 
            (event.reason.message?.includes('__retryCount') ||
             event.reason.message?.includes('axios') ||
             event.reason.config ||
             event.reason.response);
        
        if (isAxiosError) {
            console.warn('Axios-related unhandled promise rejection detected and suppressed');
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.reason, {
                    tags: {
                        errorType: 'AXIOS_UNHANDLED_REJECTION',
                        component: 'axiosConfig'
                    },
                    level: 'warning'
                });
            }
            
            // Suppress the error to prevent it from showing in console
            event.preventDefault();
            return;
        }
        
        // If the rejection value is not an Error, convert it
        if (!(event.reason instanceof Error)) {
            let errorMessage;
            
            // Handle objects with code and message properties (like wallet errors)
            if (event.reason && typeof event.reason === 'object' && event.reason.code && event.reason.message) {
                errorMessage = `Unhandled promise rejection (${event.reason.code}): ${event.reason.message}`;
            } else if (event.reason && typeof event.reason === 'object') {
                // Handle other objects by stringifying them
                errorMessage = `Unhandled promise rejection: ${JSON.stringify(event.reason)}`;
            } else {
                // Handle primitives
                errorMessage = `Unhandled promise rejection: ${String(event.reason)}`;
            }
            
            // Create error for logging - don't modify event.reason as it's read-only in Firefox
            const error = new Error(errorMessage);
        }
        
        // Log to Sentry if available
        if (window.Sentry) {
            window.Sentry.captureException(event.reason, {
                tags: {
                    errorType: 'UNHANDLED_PROMISE_REJECTION',
                    rejectionValue: String(event.reason)
                }
            });
        }
        
        // Prevent the default behavior
        event.preventDefault();
    });
}

// Request interceptor for basic validation
axiosInstance.interceptors.request.use(
    (config) => {
        // Validate config before processing
        if (!config) {
            console.warn('Axios request interceptor received undefined config');
            return Promise.reject(new Error('Invalid request configuration'));
        }
              
        // Add timestamp for debugging
        config.metadata = { startTime: Date.now() };
        
        // Return config directly (no queue)
        return config;
    },
    (error) => {
        console.error('Axios request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for retry logic
axiosInstance.interceptors.response.use(
    (response) => {
        // Add timing information for debugging
        if (response.config.metadata?.startTime) {
            const duration = Date.now() - response.config.metadata.startTime;
            console.log(`Request completed in ${duration}ms:`, response.config.url);
        }
        return response;
    },
    async (error) => {
        const config = error.config;
        
        // Check if config exists before proceeding
        if (!config) {
            console.warn('Axios error without config, cannot retry:', error.message);
            return Promise.reject(error);
        }
        
        // Initialize retry count if not exists
        if (!config.__retryCount) {
            config.__retryCount = 0;
        }

        // Check if we should retry
        if (config.__retryCount < retryConfig.retries && retryConfig.retryCondition(error)) {
            config.__retryCount++;
            
            // Calculate delay
            const delay = retryConfig.retryDelay(config.__retryCount - 1);
            
            console.log(`Retrying request (${config.__retryCount}/${retryConfig.retries}) after ${delay}ms delay. Error: ${error.response?.status || error.message}`);
            
            try {
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delay));
                
                // Retry the request
                return await axiosInstance(config);
            } catch (retryError) {
                console.error('Error during retry:', retryError);
                return Promise.reject(retryError);
            }
        }

        // Handle specific error cases
        if (error.response && error.response.status === 500) {
            // Internal server error
            console.error('Server error (500):', {
                url: config.url,
                method: config.method,
                message: error.message
            });
            
            // Log to Sentry if available
            if (typeof window !== 'undefined' && window.Sentry) {
                window.Sentry.captureException(error, {
                    tags: {
                        errorType: 'SERVER_ERROR_500',
                        endpoint: config.url,
                        method: config.method
                    },
                    extra: {
                        responseData: error.response.data,
                        requestData: config.data
                    }
                });
            }
            
            // Enhance error message for better debugging
            error.message = `Request failed with status code 500 at ${config.url}`;
        } else if (error.response && error.response.status === 429) {
            const retryAfter = error.response.headers['retry-after'];
            const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 60000; // Default 1 minute
            
            console.warn(`Rate limited. Server suggests waiting ${waitTime}ms before retrying.`);
            
            // Show user-friendly message
            if (typeof window !== 'undefined') {
                // You can integrate this with your notification system
                console.warn('Too many requests. Please wait a moment before trying again.');
            }
        } else if (!error.response) {
            // Network error - no response from server
            console.warn('Network error detected. This could be due to connectivity issues or server problems.');
            
            if (typeof window !== 'undefined') {
                console.warn('Please check your internet connection and try again.');
            }
        } else if (error.message?.includes('aborted')) {
            // Request aborted - usually due to component unmounting or user navigation
            console.warn('Request was aborted. This is usually normal when navigating away from a page.');
        } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            // Timeout error
            console.warn('Request timed out. This could be due to slow network or server issues.');
        }

        return Promise.reject(error);
    }
);

// Utility function to handle 429 errors gracefully
export const handleRateLimitError = (error, customMessage = null) => {
    if (error && error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        const waitTime = retryAfter ? parseInt(retryAfter) : 60;
        
        const message = customMessage || `Too many requests. Please wait ${waitTime} seconds before trying again.`;
        
        // You can replace this with your notification system
        console.warn(message);
        
        return {
            isRateLimited: true,
            retryAfter: waitTime,
            message: message
        };
    }
    
    return {
        isRateLimited: false,
        message: error?.message || 'An error occurred'
    };
};

// Export the configured axios instance
export default axiosInstance;

// Export the original axios for cases where you need it
export { axios as originalAxios };
